import React, { useState, useCallback, memo, useRef, useEffect } from 'react';
import Pagination from './ContactsPagination';
import ContactDetailsModal from './ContactDetailsModal';
import { contactsAPI } from 'services/apisService';

const statusOptions = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-800' },
  { value: 'attempted_contact', label: 'Attempted Contact', color: 'bg-purple-100 text-purple-800' },
  { value: 'follow_up_scheduled', label: 'Follow-up Scheduled', color: 'bg-amber-100 text-amber-800' },
  { value: 'interested', label: 'Interested', color: 'bg-green-100 text-green-800' },
  { value: 'not_interested', label: 'Not Interested', color: 'bg-red-100 text-red-800' },
  { value: 'wrong_contact', label: 'Wrong Contact', color: 'bg-gray-100 text-gray-800' },
  { value: 'converted_to_lead', label: 'Converted to Lead', color: 'bg-emerald-100 text-emerald-800' }
];

const COLUMN_WIDTHS = {
  checkbox: '48px',
  name: '220px',
  email: '220px',
  phone: '150px',
  company: '180px',
  userType: '140px', 
  title: '150px',
  location: '140px',
  source: '120px',
  createdAt: '220px',
  status: '220px'
};

const ROW_HEIGHT = '60px';
const HEADER_HEIGHT = '48px';

const formatUserType = (userType) => {
  if (!userType || userType === '--') return userType || '--';
  if (userType === 'FlexpertEnquiry') return 'Flexpert';
  return userType;
};

const TableHeader = memo(({ selectAll, handleSelectAll, showCheckboxes }) => (
  <thead>
    <tr className="bg-gray-100 border-b" style={{ height: HEADER_HEIGHT }}>
      {showCheckboxes && (
        <th className="py-3 px-4 border-r" style={{ width: COLUMN_WIDTHS.checkbox, minWidth: COLUMN_WIDTHS.checkbox }}>
          <input 
            type="checkbox" 
            checked={selectAll}
            onChange={handleSelectAll}
            className="w-4 h-4 cursor-pointer"
          />
        </th>
      )}
      <th className="py-3 px-4 text-left border-r relative text-gray-700 font-semibold text-[0.85rem]" style={{ width: COLUMN_WIDTHS.name, minWidth: COLUMN_WIDTHS.name }}>
        <div className="flex justify-between items-center">
          <span>NAME</span>
          <span className="text-gray-700 font-semibold text-lg">⋮</span>
        </div>
      </th>
      <th className="py-3 px-4 text-left border-r relative text-gray-700 font-semibold text-[0.85rem]" style={{ width: COLUMN_WIDTHS.email, minWidth: COLUMN_WIDTHS.email }}>
        <div className="flex justify-between items-center">
          <span>EMAIL</span>
          <span className="text-gray-700 font-semibold text-lg">⋮</span>
        </div>
      </th>
      <th className="py-3 px-4 text-left border-r relative text-gray-700 font-semibold text-[0.85rem]" style={{ width: COLUMN_WIDTHS.phone, minWidth: COLUMN_WIDTHS.phone }}>
        <div className="flex justify-between items-center">
          <span>PHONE NUMBER</span>
          <span className="text-gray-700 font-semibold text-lg">⋮</span>
        </div>
      </th>
      <th className="py-3 px-4 text-left border-r relative text-gray-700 font-semibold text-[0.85rem]" style={{ width: COLUMN_WIDTHS.company, minWidth: COLUMN_WIDTHS.company }}>
        <div className="flex justify-between items-center">
          <span>COMPANY NAME</span>
          <span className="text-gray-700 font-semibold text-lg">⋮</span>
        </div>
      </th>
      <th className="py-3 px-4 text-left border-r relative text-gray-700 font-semibold text-[0.85rem]" style={{ width: COLUMN_WIDTHS.userType, minWidth: COLUMN_WIDTHS.userType }}>
      <div className="flex justify-between items-center">
        <span>USER TYPE</span>
        <span className="text-gray-700 font-semibold text-lg">⋮</span>
      </div>
    </th>
      <th className="py-3 px-4 text-left border-r relative text-gray-700 font-semibold text-[0.85rem]" style={{ width: COLUMN_WIDTHS.title, minWidth: COLUMN_WIDTHS.title }}>
        <div className="flex justify-between items-center">
          <span>TITLE</span>
          <span className="text-gray-700 font-semibold text-lg">⋮</span>
        </div>
      </th>
      <th className="py-3 px-4 text-left border-r relative text-gray-700 font-semibold text-[0.85rem]" style={{ width: COLUMN_WIDTHS.location, minWidth: COLUMN_WIDTHS.location }}>
        <div className="flex justify-between items-center">
          <span>LOCATION</span>
          <span className="text-gray-700 font-semibold text-lg">⋮</span>
        </div>
      </th>
      <th className="py-3 px-4 text-left border-r relative text-gray-700 font-semibold text-[0.85rem]" style={{ width: COLUMN_WIDTHS.source, minWidth: COLUMN_WIDTHS.source }}>
        <div className="flex justify-between items-center">
          <span>SOURCE</span>
          <span className="text-gray-700 font-semibold text-lg">⋮</span>
        </div>
      </th>
      <th className="py-3 px-4 text-left border-r relative text-gray-700 font-semibold text-[0.85rem]" style={{ width: COLUMN_WIDTHS.createdAt, minWidth: COLUMN_WIDTHS.createdAt }}>
        <div className="flex justify-between items-center">
          <span>CREATED AT</span>
          <span className="text-gray-700 font-semibold text-lg">⋮</span>
        </div>
      </th>
    </tr>
  </thead>
));

const StatusTableHeader = memo(() => (
  <thead>
    <tr className="bg-gray-100 border-b" style={{ height: HEADER_HEIGHT }}>
      <th className="py-3 px-4 text-left relative text-gray-700 font-semibold text-[0.85rem]" style={{ width: COLUMN_WIDTHS.status, minWidth: COLUMN_WIDTHS.status }}>
        <div className="flex justify-between items-center">
          <span>LEAD STATUS</span>
          <span className="text-gray-700 font-semibold text-lg">⋮</span>
        </div>
      </th>
    </tr>
  </thead>
));

const StatusDropdown = memo(({ contactId, currentStatus, updatingStatus, onStatusChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownDirection, setDropdownDirection] = useState('down');
  const dropdownRef = useRef(null);
  const currentOption = statusOptions.find(opt => opt.value === currentStatus) || statusOptions[0];
  
  const handleOptionClick = useCallback((value) => {
    setIsOpen(false);
    if (value !== currentStatus) {
      onStatusChange(contactId, value);
    }
  }, [contactId, currentStatus, onStatusChange]);

  const handleToggleDropdown = useCallback(() => {
    if (!isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = statusOptions.length * 40 + 16; 
      
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        setDropdownDirection('up');
      } else {
        setDropdownDirection('down');
      }
    }
    setIsOpen(!isOpen);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
  
  const handleScroll = () => {
    setIsOpen(false);
  };
  
  document.addEventListener('mousedown', handleClickOutside);
  document.addEventListener('scroll', handleScroll, true); 
  
  return () => {
    document.removeEventListener('mousedown', handleClickOutside);
    document.removeEventListener('scroll', handleScroll, true);
  };
}, [isOpen]);

  if (updatingStatus === contactId) {
    return (
      <div className="flex justify-center">
        <div className="animate-spin h-4 w-4 border-t-2 border-[#3985b6] rounded-full"></div>
      </div>
    );
  }


  return (
    <div className="relative" ref={dropdownRef}>
      <div 
        onClick={handleToggleDropdown}
        className={`flex items-center justify-between cursor-pointer border rounded-md min-w-[180px] px-2 py-1.5 ${currentOption.color} hover:opacity-90 transition-all duration-200`}
      >
        <span className="text-sm font-medium">{currentOption.label}</span>
        <svg className={`ml-2 h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </div>
      
      {isOpen && (
        <div 
          className="fixed z-50 bg-white border rounded-md shadow-lg"
          style={{
            width: '180px',
            top: dropdownDirection === 'up' ? 
              `${dropdownRef.current.getBoundingClientRect().top - (statusOptions.length * 37)}px` : 
              `${dropdownRef.current.getBoundingClientRect().bottom}px`,
            left: `${dropdownRef.current.getBoundingClientRect().left}px`
          }}
        >
          {statusOptions.map(option => (
            <div
              key={option.value}
              className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 ${
                option.value === currentStatus ? 'bg-gray-50' : ''
              }`}
              onClick={() => handleOptionClick(option.value)}
            >
              <div className={`inline-block w-2 h-2 rounded-full mr-2 ${option.color.split(' ')[0]}`}></div>
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

const formatCreatedAt = (createdAt) => {
  if (!createdAt) return { time: '--', date: '' };
  const d = new Date(createdAt);
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const date = d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  return { time, date };
};

const ContactRow = memo(({ contact, handleSelectContact, onContactClick, showCheckboxes }) => (
  <tr key={contact.id} className="border-b hover:bg-gray-50" style={{ height: ROW_HEIGHT }}>
    {showCheckboxes && (
      <td className="border-r" style={{ width: COLUMN_WIDTHS.checkbox, minWidth: COLUMN_WIDTHS.checkbox }}>
        <div className="h-full flex items-center px-4">
          <input 
            type="checkbox" 
            checked={contact.selected}
            onChange={() => handleSelectContact(contact.id)}
            className="w-4 h-4 cursor-pointer"
          />
        </div>
      </td>
    )}
    <td className="border-r" style={{ width: COLUMN_WIDTHS.name, minWidth: COLUMN_WIDTHS.name }}>
  <div className="h-full flex items-center px-4">
    <div className="flex items-center w-full">
      <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center text-white mr-2 flex-shrink-0">
        {contact.name.charAt(0)}
      </div>
      <span 
        className="text-[#3985b6] cursor-pointer hover:underline truncate"
        onClick={() => onContactClick(contact.id)} 
        title={contact.name}
        style={{ maxWidth: '150px' }} 
      >
        {contact.name}
      </span>
      {/* Domain icon */}
      {contact.domainExists && (
        <div className="ml-2 flex-shrink-0" title="Domain exists">
         <svg className="w-4 h-4 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
        </svg>
        </div>
      )}
    </div>
  </div>
</td>
    <td className="border-r text-[#3985b6] truncate" style={{ width: COLUMN_WIDTHS.email, minWidth: COLUMN_WIDTHS.email }} title={contact.email}>
      <div className="h-full flex items-center px-4">
        {contact.email}
      </div>
    </td>
    <td className="border-r text-gray-800 truncate" style={{ width: COLUMN_WIDTHS.phone, minWidth: COLUMN_WIDTHS.phone }} title={contact.phone}>
      <div className="h-full flex items-center px-4">
        {contact.phone}
      </div>
    </td>
    <td className="border-r text-gray-800 truncate" style={{ width: COLUMN_WIDTHS.company, minWidth: COLUMN_WIDTHS.company }} title={contact.company}>
      <div className="h-full flex items-center px-4">
        {contact.company}
      </div>
    </td>
    <td className="border-r text-gray-800 truncate" style={{ width: COLUMN_WIDTHS.userType, minWidth: COLUMN_WIDTHS.userType }} title={formatUserType(contact.userType)}>
      <div className="h-full flex items-center px-4">
        {formatUserType(contact.userType)}
      </div>
    </td>
    <td className="border-r text-gray-800 truncate" style={{ width: COLUMN_WIDTHS.title, minWidth: COLUMN_WIDTHS.title }} title={contact.title || '--'}>
      <div className="h-full flex items-center px-4">
        {contact.title || '--'}
      </div>
    </td>
    <td className="border-r text-gray-800 truncate" style={{ width: COLUMN_WIDTHS.location, minWidth: COLUMN_WIDTHS.location }} title={contact.location || '--'}>
      <div className="h-full flex items-center px-4">
        {contact.location || '--'}
      </div>
    </td>
    <td className="border-r text-gray-800 truncate" style={{ width: COLUMN_WIDTHS.source, minWidth: COLUMN_WIDTHS.source }} title={contact.source || '--'}>
      <div className="h-full flex items-center px-4">
        {contact.source || '--'}
      </div>
    </td>
    <td className="border-r text-gray-800" style={{ width: COLUMN_WIDTHS.createdAt, minWidth: COLUMN_WIDTHS.createdAt }}>
      <div className="h-full flex items-center px-4">
        {(() => {
          const { time, date } = formatCreatedAt(contact.createdAt);
          if (time === '--') {
            return <span className="text-gray-400">--</span>;
          }
          return (
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-700">{time}</span>
              <span className="text-sm font-medium text-gray-600">{date}</span>
            </div>
          );
        })()}
      </div>
    </td>
  </tr>
));

const StatusRow = memo(({ contact, updatingStatus, handleStatusChange }) => (
  <tr key={`status-${contact.id}`} className="border-b hover:bg-gray-50" style={{ height: ROW_HEIGHT }}>
    <td className="text-gray-800" style={{ width: COLUMN_WIDTHS.status, minWidth: COLUMN_WIDTHS.status }}>
      <div className="h-full flex items-center px-4">
        <StatusDropdown 
          contactId={contact.id}
          currentStatus={contact.status || 'new'}
          updatingStatus={updatingStatus}
          onStatusChange={handleStatusChange}
        />
      </div>
    </td>
  </tr>
));

const ContactsTable = ({ 
  contacts, 
  selectAll, 
  handleSelectAll, 
  handleSelectContact, 
  updateContactStatus,
  currentPage,
  totalPages,
  onPageChange,
  showCheckboxes,
  activeFilters,
  fetchContacts,
  currentLimit,     
  onLimitChange,   
  totalContacts,
  setContacts    
}) => {
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  
  const handleStatusChange = useCallback(async (contactId, newStatus) => {
    setUpdatingStatus(contactId);
    
    try {
      await updateContactStatus(contactId, newStatus);
      
      if (newStatus === 'converted_to_lead') {
        fetchContacts(currentPage, activeFilters, currentLimit); 
      }
    } catch (error) {
      console.error('Error updating contact status:', error);
      alert('Failed to update status. Please try again.');
    } finally {
      setUpdatingStatus(null);
    }
  }, [updateContactStatus, currentPage, activeFilters, fetchContacts, currentLimit]);

const handleContactClick = useCallback(async (contactId) => {
  try {
    const contactData = await contactsAPI.getContact(contactId); 
    setSelectedContact(contactData); 
    setShowModal(true);
  } catch (error) {
    console.error('Error fetching contact details:', error);
    alert('Failed to load contact details. Please try again.');
  }
}, []);


  const handleCloseModal = useCallback(() => {
    setShowModal(false);
  }, []);

 const handleModalNavigation = useCallback(async (direction) => {
  const currentIndex = contacts.findIndex(c => c.id === selectedContact.id);
  let newIndex;
  
  if (direction === 'next') {
    newIndex = (currentIndex + 1) % contacts.length;
  } else {
    newIndex = (currentIndex - 1 + contacts.length) % contacts.length;
  }
  
  const nextContact = contacts[newIndex];
  
  try {
    const fullContactData = await contactsAPI.getContact(nextContact.id);
    setSelectedContact(fullContactData);
  } catch (error) {
    console.error('Error fetching contact details:', error);
    setSelectedContact(nextContact);
  }
}, [contacts, selectedContact]);

  const handleContactUpdate = useCallback((updatedContactData) => {
    setContacts(prevContacts => 
      prevContacts.map(contact => 
        contact.id === updatedContactData.id 
          ? {
              ...contact,
              name: updatedContactData.name || contact.name,
              email: updatedContactData.email || contact.email,
              phone: updatedContactData.phone || contact.phone,
              company: updatedContactData.companyName || contact.company,
              title: updatedContactData.title || contact.title,
              location: updatedContactData.location || contact.location,
              source: updatedContactData.source || contact.source,
              userType: updatedContactData.userType || contact.userType,
              status: updatedContactData.status || contact.status
            }
          : contact
      )
    );
    
    setSelectedContact(prevSelected => ({
      ...prevSelected,
      ...updatedContactData
    }));
  }, [setContacts]);


  return (
    <div>
      <div className="border rounded-md">
        <div className="overflow-x-auto rounded-[5px]">
          <div className="flex" style={{ minWidth: 'max-content' }}>
            <div className="border-r">
              <table className="bg-white border-collapse" style={{ tableLayout: 'fixed', borderSpacing: 0 }}>
                <TableHeader selectAll={selectAll} handleSelectAll={handleSelectAll} showCheckboxes={showCheckboxes}/>
                <tbody>
                  {contacts.map((contact) => (
                    <ContactRow
                      key={contact.id}
                      contact={contact}
                      handleSelectContact={handleSelectContact}
                      onContactClick={handleContactClick}
                      showCheckboxes={showCheckboxes}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="bg-white sticky right-0 shadow-lg" style={{ width: COLUMN_WIDTHS.status }}>
              <table className="w-full bg-white border-collapse" style={{ tableLayout: 'fixed', borderSpacing: 0 }}>
                <StatusTableHeader />
                <tbody>
                  {contacts.map((contact) => (
                    <StatusRow
                      key={contact.id}
                      contact={contact}
                      updatingStatus={updatingStatus}
                      handleStatusChange={handleStatusChange}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      
      {totalPages > 0 && (
        <Pagination 
          currentPage={currentPage} 
          totalPages={totalPages} 
          onPageChange={onPageChange}
          currentLimit={currentLimit}     
          onLimitChange={onLimitChange}   
          totalContacts={totalContacts}   
        />
      )}

      <ContactDetailsModal
        isOpen={showModal}
        onClose={handleCloseModal}
        contact={selectedContact}
        onNavigate={handleModalNavigation}
        contacts={contacts}
        onContactUpdate={handleContactUpdate} 
      />
    </div>
  );
};

export default ContactsTable;